"use client";

import CircularButton from "@/components/CircularButton";
import CircularRectTextButton from "@/components/CircularButton/styled/CircularRectTextButton";
import { FlexColumn, FlexRowAlignCenter } from "@/components/Flex";
import br from "@/styles/breakpoints.module.css";
import { borderRadius } from "@/utils/borderRadius";
import { spacing } from "@/utils/spacing";
import { themeColors } from "@/utils/themeColors";
import { AI_LANDING_TOOLS, AI_TOOLS, AI_TOOLS_CATEGORIES } from "@knowt/syncing/constants/ai-tools";
import {
    ArrowDown,
    ChartNoAxesGanttIcon,
    EyeIcon,
    PencilIcon,
    PodcastIcon,
    ReceiptTextIcon,
    SearchIcon,
    SparkleIcon,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import TeacherAIToolCard from "./TeacherAIToolCard";
import FavoriteAITools from "./FavoriteAITools";
import RepeatAutoFillGrid from "@/components/styled/div/RepeatAutoFillGrid";

const CATEGORY_ICON = {
    [AI_TOOLS_CATEGORIES.PLANNING]: ChartNoAxesGanttIcon,
    [AI_TOOLS_CATEGORIES.SUMMARIZERS]: ReceiptTextIcon,
    [AI_TOOLS_CATEGORIES.CONTENT]: PencilIcon,
    [AI_TOOLS_CATEGORIES.REVIEW]: EyeIcon,
    [AI_TOOLS_CATEGORIES.COMMUNICATION]: PodcastIcon,
};

const CategoryButton = ({
    id,
    icon: IconComponent,
    label,
    isActive,
    onClick,
}: { id?: string; icon: React.ComponentType<any>; label: string; isActive: boolean; onClick: () => void }) => {
    return (
        <CircularRectTextButton
            id={id}
            className="secondaryTextBold1"
            onClick={onClick}
            style={{
                gap: "0.5rem",
                minWidth: "10rem",
                padding: `${spacing.XS_2} ${spacing.SM}`,
                color: isActive ? themeColors.neutralWhite : themeColors.neutralBlack,
                backgroundColor: isActive ? themeColors.neutralBlack : themeColors.neutralWhite,
            }}>
            <IconComponent size={24} color={isActive ? themeColors.neutralWhite : themeColors.neutralBlack} />
            {label}
        </CircularRectTextButton>
    );
};

const FilterBar = ({ currentCategory, setCurrentCategory, searchText, setSearchText }) => {
    const allCategories = Object.values(AI_TOOLS_CATEGORIES);

    return (
        <FlexRowAlignCenter
            className={br.mdDownColumn}
            style={{ justifyContent: "space-between", alignItems: "flex-end" }}>
            <FlexRowAlignCenter style={{ gap: spacing.XS_2, flexWrap: "wrap" }}>
                <CategoryButton
                    icon={SparkleIcon}
                    key={"AllCategories"}
                    label={"All"}
                    isActive={!currentCategory}
                    onClick={() => setCurrentCategory(null)}
                />
                {allCategories.map(category => (
                    <CategoryButton
                        id={`ai-tools-filter-bar-${category}`}
                        icon={CATEGORY_ICON[category]}
                        key={category}
                        label={category}
                        isActive={currentCategory === category}
                        onClick={() => setCurrentCategory(category)}
                    />
                ))}
            </FlexRowAlignCenter>

            <FlexRowAlignCenter
                className={br.lgDownDisplayNone}
                style={{
                    gap: spacing.XS,
                    padding: spacing.SM,
                    backgroundColor: themeColors.neutralWhite,
                    borderRadius: borderRadius.shortInput,
                }}>
                <SearchIcon size={20} color={themeColors.neutral2} />
                <input
                    placeholder="Find the right teacher tool"
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                    style={{
                        font: "600 1.5rem var(--knowt-font-name)",
                        outline: "none",
                        color: themeColors.neutralBlack,
                        backgroundColor: "transparent",
                        border: "none",
                        width: "25rem",
                    }}
                />
            </FlexRowAlignCenter>
        </FlexRowAlignCenter>
    );
};

const AIToolPicker = () => {
    const toolsContainerRef = useRef<HTMLDivElement>(null);
    const allAiTools = [...Object.values(AI_LANDING_TOOLS), ...Object.values(AI_TOOLS)];

    const [searchText, setSearchText] = useState<string>("");
    const [filteredCategory, setFilteredCategory] = useState<AI_TOOLS_CATEGORIES | null>(null);

    const [showScrollButton, setShowScrollButton] = useState<boolean>(true);

    const filteredSortedAiTools = useMemo(() => {
        const filteredAiTools = filteredCategory
            ? allAiTools.filter(tool => tool.category === filteredCategory)
            : allAiTools;

        const searchedFilteredAiTools = !searchText
            ? filteredAiTools
            : filteredAiTools.filter(tool => tool.title.toLowerCase().includes(searchText.toLowerCase()));

        const limitedSearchedFilteredAiTools = searchedFilteredAiTools;

        return limitedSearchedFilteredAiTools;
    }, [filteredCategory, searchText]);

    useEffect(() => {
        let buttonHidden = false;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (!buttonHidden) {
                    const shouldShowButton = !entry.isIntersecting;
                    if (!shouldShowButton) {
                        buttonHidden = true;
                        setShowScrollButton(false);
                    } else {
                        setShowScrollButton(true);
                    }
                }
            },
            { root: null, rootMargin: "0px", threshold: 1.0 }
        );

        const target = toolsContainerRef.current;
        if (target) {
            observer.observe(target);
        }

        const handleScroll = () => {
            const containerBottom = target?.getBoundingClientRect().bottom;

            if (containerBottom) {
                const isContainerFullyScrolled = containerBottom <= window.innerHeight;
                const isAboveEnd = containerBottom > window.innerHeight;

                if (isContainerFullyScrolled) {
                    buttonHidden = true;
                    setShowScrollButton(false);
                }

                if (isAboveEnd) {
                    buttonHidden = false;
                    setShowScrollButton(true);
                }
            }
        };

        window.addEventListener("scroll", handleScroll);

        return () => {
            if (target) {
                observer.unobserve(target);
            }
            window.removeEventListener("scroll", handleScroll);
        };
    }, []);

    const scrollDown = () => {
        setShowScrollButton(false);
        if (toolsContainerRef.current) {
            toolsContainerRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
        }
    };

    return (
        <>
            <FlexColumn style={{ gap: spacing.XL, position: "relative" }}>
                <FavoriteAITools />
                <FlexColumn style={{ gap: spacing.MD, width: "100%" }}>
                    <FilterBar
                        currentCategory={filteredCategory}
                        setCurrentCategory={setFilteredCategory}
                        searchText={searchText}
                        setSearchText={setSearchText}
                    />
                    <RepeatAutoFillGrid
                        ref={toolsContainerRef}
                        itemMinWidth="29rem"
                        style={{ gap: "1.2rem", position: "relative" }}>
                        {filteredSortedAiTools.map(tool => (
                            <TeacherAIToolCard key={tool.title} tool={tool} />
                        ))}
                        {showScrollButton && (
                            <CircularButton
                                onClick={scrollDown}
                                style={{
                                    position: "fixed",
                                    bottom: 20,
                                    left: "48.8%",
                                    width: "5rem",
                                    height: "5rem",
                                    backgroundColor: themeColors.neutralWhite,
                                    boxShadow: "0px 3px 35px 0px rgba(0, 0, 0, 0.15)",
                                }}>
                                <ArrowDown size={25} color={themeColors.neutralBlack} />
                            </CircularButton>
                        )}
                    </RepeatAutoFillGrid>
                </FlexColumn>
            </FlexColumn>
        </>
    );
};

export default AIToolPicker;
